import { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext, SttTabs, SttPage } from '../../../sporttia/all';
import ConfigurationMain from './ConfigurationMain';
import ConfigurationConsents from './ConfigurationConsents';
import ConfigurationRecharges from './ConfigurationRecharges';
import ConfigurationPrivileges from './ConfigurationPrivileges';
import ConfigurationNotifications from './ConfigurationNotifications';
import ConfigurationBilling from './ConfigurationBilling';
import ConfigurationWeb from './ConfigurationWeb';
import ConfigurationMandatoryData from './ConfigurationMandatoryData';
import ConfigurationPOS from './ConfigurationPOS';
import ConfigurationCashdro from './ConfigurationCashdro';

export default function Configuration() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { tab: urlTabKey } = useParams<{ tab?: string }>();
    const [scModules, setSCModules] = useState<string[]>();

    const loadSportcenter = () => {
        cxt.api('GET', `/scs/${cxt.sc?.id}`, {
            success: (r: { sc: { modules: string[] } }) => {
                setSCModules(r.sc.modules);
            },
        });
    };

    useEffect(() => {
        if (cxt.sc?.modules) {
            setSCModules(cxt.sc?.modules);
        } else {
            loadSportcenter();
        }
    }, []);

    // Define tabs configuration
    const tabsConfig = useMemo(
        () => [
            { key: 'data', show: true },
            { key: 'notifications', show: true },
            { key: 'recharges', show: true },
            { key: 'privileges', show: true },
            { key: 'consents', show: true },
            { key: 'billing', show: true },
            { key: 'web', show: scModules?.includes('web') ?? false },
            { key: 'mandatory', show: true },
            {
                key: 'pos',
                show: scModules?.includes('dataphoneConnected') ?? false,
            },
            {
                key: 'cashdro',
                show: scModules?.includes('cashdroConnected') ?? false,
            },
        ],
        [scModules],
    );

    // Get only visible tab keys
    const visibleTabKeys = useMemo(
        () => tabsConfig.filter((tab) => tab.show).map((tab) => tab.key),
        [tabsConfig],
    );

    // Calculate initial tab index from URL key
    const computedInitialTab = useMemo(() => {
        if (urlTabKey) {
            const index = visibleTabKeys.indexOf(urlTabKey);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [urlTabKey, visibleTabKeys]);

    // Handle tab change - update URL
    const handleTabChange = (tabIndex: number) => {
        const tabKey = visibleTabKeys[tabIndex];
        if (tabKey) {
            navigate(`/configurations/${tabKey}`, { replace: true });
        }
    };

    const tabs = [
        {
            key: 'data',
            caption: t('Data'),
            component: <ConfigurationMain />,
        },
        {
            key: 'notifications',
            caption: t('Notifications'),
            component: <ConfigurationNotifications />,
        },
        {
            key: 'recharges',
            caption: t('Recharges'),
            component: <ConfigurationRecharges />,
        },
        {
            key: 'privileges',
            caption: t('Privileges'),
            component: <ConfigurationPrivileges />,
        },
        {
            key: 'consents',
            caption: t('Consents'),
            component: <ConfigurationConsents />,
        },
        {
            key: 'billing',
            caption: t('Billing'),
            component: <ConfigurationBilling />,
        },
        {
            key: 'web',
            show: scModules?.includes('web') ?? false,
            caption: 'Web',
            component: <ConfigurationWeb />,
        },
        {
            key: 'mandatory',
            caption: t('MandatoryFields'),
            component: <ConfigurationMandatoryData />,
        },
        {
            key: 'pos',
            show: scModules?.includes('dataphoneConnected') ?? false,
            caption: t('PF.DATAFONO'),
            component: <ConfigurationPOS />,
        },
        {
            key: 'cashdro',
            show: scModules?.includes('cashdroConnected') ?? false,
            caption: 'CashDro',
            component: <ConfigurationCashdro />,
        },
    ];

    return (
        <SttPage title={t('Configuration')}>
            <SttTabs
                tabs={tabs}
                initialTab={computedInitialTab}
                onTabChange={handleTabChange}
                grow
            />
        </SttPage>
    );
}
